import { Drawer, DrawerContextProvider, DrawerMode, ModalConfirmation, useModal } from '@billy/design-system'
import { ReactElement, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { BankLine } from '@modules-deprecated/bankReconciliation/types'

import { ModalId } from '../../../../../../enums/modalId'
import { useToggleBookkeepingHero } from '../../../../../../hooks/useToggleBookkeepingHero'
import { useBankTransactionDrawer, useSandboxModeContext } from '../../contexts'
import { useBookkeepingItemsContext } from '../../contexts/bookkeepingItemsContext'
import { useSandboxGuardedHandler } from '../../hooks/useSandboxGuardedHandler'
import { DefaultState } from './elements/DefaultState/DefaultState'
import * as Styled from './elements/DefaultState/styles'
import { DrawerFooter } from './elements/DrawerFooter'
import { DrawerHeader } from './elements/DrawerHeader'
import { BookkeepingItemsList } from './elements/ListElements/BookkeepingItemsList'
import { TransactionsList } from './elements/ListElements/TransactionsList/TransactionsList'
import { MatchedState } from './elements/MatchedState'
import { SuggestionItem } from './elements/SuggestionItem'
import { DrawerView } from './enums/drawerView'

interface BankTransactionDrawerProps {
  onCreateManualPaymentClick: (transaction: BankLine) => void
  /**
   * Portals the drawer into this element instead of `document.body` — used by the sandbox
   * pane to keep the drawer confined within its modal-style card. Omitted on the real route.
   */
  portalContainer?: Element
}

export const BankTransactionDrawer = ({
  onCreateManualPaymentClick,
  portalContainer,
}: BankTransactionDrawerProps): ReactElement | null => {
  const { t } = useTranslation()
  const {
    selectedTransaction,
    associatedBookkeepingItems,
    hasBankReconciliationAutoMatchScope,
    combinedTransactions,
    linkedBankLines,
    isDrawerCloseBlocked,
    clearSelection,
    selectedBookkeepingItemIds,
    suggestedBookkeepingItem,
    matchSuggestion,
    isSuggestionLoading,
    isMatchedStateLoading,
    isPrivate,
  } = useBankTransactionDrawer()
  const { resetFilters } = useBookkeepingItemsContext()
  const isSandboxMode = !!useSandboxModeContext()
  const { isBookkeepingHeroHidden, toggleBookkeepingHero } = useToggleBookkeepingHero()

  const { open: openDiscardModal, close: closeDiscardModal } = useModal(ModalId.BankTransactionDrawerDiscardModal)

  const [activeStep, setActiveStep] = useState<DrawerView>(DrawerView.Default)

  const panelMode = selectedTransaction ? DrawerMode.Opened : DrawerMode.Closed
  const isDrawerOpen = panelMode === DrawerMode.Opened

  const showMatchedState = selectedTransaction?.match?.isApproved === true
  const shouldShowSuggestion =
    hasBankReconciliationAutoMatchScope && ((!showMatchedState && !!suggestedBookkeepingItem) || isSuggestionLoading)
  const hasPendingChanges = selectedBookkeepingItemIds.size > 0 || combinedTransactions.size > 0 || isPrivate

  const closeDrawer = useCallback(() => {
    clearSelection()
    setActiveStep(DrawerView.Default)
    resetFilters()
  }, [clearSelection, resetFilters])

  const handlePanelModeChange = useCallback(
    (mode: DrawerMode) => {
      if (mode === DrawerMode.Closed) {
        closeDrawer()
      }
    },
    [closeDrawer],
  )

  const handleCloseRequest = useCallback(() => {
    if (hasPendingChanges) {
      openDiscardModal()
    } else {
      closeDrawer()
    }
  }, [closeDrawer, hasPendingChanges, openDiscardModal])

  const handleDiscardConfirm = useCallback(() => {
    closeDiscardModal()
    closeDrawer()
  }, [closeDiscardModal, closeDrawer])

  const handleViewAllItemsClick = useCallback(() => {
    setActiveStep(DrawerView.AllBookkeepingItems)
  }, [])

  const handleCreateManualPaymentClickReal = useCallback(() => {
    if (selectedTransaction) {
      onCreateManualPaymentClick(selectedTransaction)
      handlePanelModeChange(DrawerMode.Closed)
    }
  }, [handlePanelModeChange, onCreateManualPaymentClick, selectedTransaction])

  const handleCreateManualPaymentClick = useSandboxGuardedHandler(isSandboxMode, handleCreateManualPaymentClickReal)

  const handleViewTransactionsClick = useCallback(() => {
    setActiveStep(DrawerView.AllTransactions)
  }, [])

  const handleStepBack = useCallback(() => {
    setActiveStep(DrawerView.Default)
  }, [])

  const renderInitialState = useCallback(() => {
    if (showMatchedState) {
      return (
        <MatchedState
          associatedBookkeepingItems={associatedBookkeepingItems}
          isLoading={isMatchedStateLoading}
          linkedBankLines={linkedBankLines}
        />
      )
    }

    if (shouldShowSuggestion) {
      return (
        <SuggestionItem
          bankLine={selectedTransaction}
          matchSuggestion={matchSuggestion}
          suggestedBookkeepingItem={suggestedBookkeepingItem}
          isLoading={isSuggestionLoading}
        />
      )
    }

    return (
      <DefaultState
        transactionsSide={selectedTransaction?.side}
        handleViewAllItemsClick={handleViewAllItemsClick}
        handleCreateManualPaymentClick={handleCreateManualPaymentClick}
        handleViewTransactionsClick={handleViewTransactionsClick}
      />
    )
  }, [
    associatedBookkeepingItems,
    isMatchedStateLoading,
    linkedBankLines,
    handleCreateManualPaymentClick,
    handleViewAllItemsClick,
    handleViewTransactionsClick,
    isSuggestionLoading,
    shouldShowSuggestion,
    showMatchedState,
    suggestedBookkeepingItem,
    matchSuggestion,
    selectedTransaction,
  ])

  useEffect(() => {
    if (!isDrawerOpen) {
      return
    }

    const wasBookkeepingHeroHidden = isBookkeepingHeroHidden
    toggleBookkeepingHero(true)

    return () => {
      toggleBookkeepingHero(wasBookkeepingHeroHidden)
    }
    // Capture hide state at open time only. Including isBookkeepingHeroHidden would
    // re-run after we hide and persist the hidden value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDrawerOpen, toggleBookkeepingHero])

  return (
    <DrawerContextProvider
      drawerMode={panelMode}
      onCloseRequest={handleCloseRequest}
      onDrawerModeChange={handlePanelModeChange}
    >
      <Drawer closable={!isDrawerCloseBlocked} portalContainer={portalContainer}>
        <Drawer.Steps activeStep={activeStep} onBack={handleStepBack} onStepChange={setActiveStep}>
          <Drawer.Step customContent={<DrawerHeader />}>
            <Drawer.Body>
              <Styled.LargeDivider />
              {renderInitialState()}
            </Drawer.Body>
          </Drawer.Step>

          <Drawer.Step title={t('bank_transactions.drawer.all_items_title')}>
            <Drawer.Body>
              <BookkeepingItemsList showAll />
            </Drawer.Body>
          </Drawer.Step>

          <Drawer.Step title={t('bank_transactions.drawer.transactions.title')}>
            <Drawer.Body>
              <TransactionsList showAll />
            </Drawer.Body>
          </Drawer.Step>
        </Drawer.Steps>

        <DrawerFooter activeStep={activeStep} setActiveStep={setActiveStep} onMatchSuccess={closeDrawer} />
      </Drawer>

      <ModalConfirmation
        cancelLabel={t('bank_transactions.drawer.discard_modal.cancel')}
        danger
        id={ModalId.BankTransactionDrawerDiscardModal}
        message={t('bank_transactions.drawer.discard_modal.message')}
        okLabel={t('bank_transactions.drawer.discard_modal.confirm')}
        onOk={handleDiscardConfirm}
        title={t('bank_transactions.drawer.discard_modal.title')}
      />
    </DrawerContextProvider>
  )
}
